import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { horizontalCap, prismSolid, pyramidOverRing, ringPoints } from '../solids';
import { buildVesselRoof } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { centredWindow, gableWall, pointedWindows, rectWall, type MasonryStyles } from './masonry';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf, spanOver } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** The retained outline is a surviving building survey, not a recovered medieval elevation.
 * A long narrow body avoids the generic fitter's short, wide hall. Heights are estimates. */
export function agnietenPlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const bounds = planBounds(plan),
    u0 = bounds.u0 + 1.5,
    u1 = bounds.u1 - 2;
  const span = spanOver(plan, u0, u1, 0);
  if (!span) throw new Error('Agnietenkapel has no continuous nave inside its reserved outline');
  const vessel: VesselPlan = {
    u0,
    chordU: u1,
    vc: (span[0] + span[1]) / 2,
    half: (span[1] - span[0]) / 2 - 0.1,
    eaveY: 10.8,
    ridgeY: 16.4,
    apse: 'flat',
  };
  return {
    plan,
    frame: meshFrameOf(plan),
    vessel,
    galleryEnd: u0 + (u1 - u0) * 0.64,
    galleryY: 5.1,
  };
}

function gallery(
  builder: MeshBuilder,
  styles: MasonryStyles,
  planned: ReturnType<typeof agnietenPlan>
) {
  const { frame, vessel: v, galleryEnd: end, galleryY: y } = planned;
  const lo = v.vc - v.half + 0.08,
    hi = v.vc + v.half - 0.08;
  const wood = { ...styles.wall, surface: SURFACE.wood, color: [0.21, 0.15, 0.09] as const };
  const ring: Ring = [
    [v.u0 + 0.08, lo],
    [end, lo],
    [end, hi],
    [v.u0 + 0.08, hi],
  ];
  // The east choir remains open from the lower church into the roof. A full floor would
  // backdate the conversion for the Athenaeum in 1632.
  prismSolid(builder, wood, frame, ring, y - 0.22, y, { top: true, bottom: true });
  for (let vc = lo + 0.2; vc < hi; vc += 0.75)
    prismSolid(
      builder,
      wood,
      frame,
      [
        [end - 0.09, vc - 0.04],
        [end, vc - 0.04],
        [end, vc + 0.04],
        [end - 0.09, vc + 0.04],
      ],
      y,
      y + 0.95
    );
  for (const top of [y + 0.14, y + 0.91])
    prismSolid(
      builder,
      wood,
      frame,
      [
        [end - 0.12, lo],
        [end + 0.02, lo],
        [end + 0.02, hi],
        [end - 0.12, hi],
      ],
      top,
      top + 0.08
    );
}

function belfry(
  builder: MeshBuilder,
  styles: MasonryStyles,
  planned: ReturnType<typeof agnietenPlan>
) {
  const { frame, vessel: v } = planned;
  const center: Vec2 = [v.u0 + (v.chordU - v.u0) * 0.6, v.vc];
  const ring = ringPoints(center, 0.65, 8, Math.PI / 8);
  const base = v.ridgeY - 0.7,
    top = v.ridgeY + 1.8;
  ring.forEach((a, i) => {
    rectWall(
      builder,
      styles,
      frame,
      [a, ring[(i + 1) % 8]!],
      [base, top],
      centredWindow(
        Math.hypot(ring[(i + 1) % 8]![0] - a[0], ring[(i + 1) % 8]![1] - a[1]),
        1,
        2.15,
        0.55
      )
    );
  });
  pyramidOverRing(builder, styles, frame, ring, top, { plan: center, y: top + 3.2 });
}

export function buildAgnietenkapel(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors,
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY
): number {
  const before = builder.triangleCount;
  const styles = paintedChapelStyles(colors, ids, tracery),
    p = agnietenPlan(landmark);
  const { frame, vessel: v } = p,
    lo = v.vc - v.half,
    hi = v.vc + v.half;
  const width = 2 * v.half,
    heights = { eaveY: v.eaveY, apexY: v.ridgeY };
  gableWall(
    builder,
    styles,
    frame,
    [
      [v.u0, hi],
      [v.u0, lo],
    ],
    heights,
    [...centredWindow(width, 0, 3.4, 0.22), ...centredWindow(width, 6.1, 10, 0.45)]
  );
  gableWall(
    builder,
    styles,
    frame,
    [
      [v.chordU, lo],
      [v.chordU, hi],
    ],
    heights,
    centredWindow(width, 2.3, 10, 0.45)
  );
  for (const sign of [-1, 1]) {
    const vc = v.vc + sign * v.half;
    const a: Vec2 = sign < 0 ? [v.u0, vc] : [v.chordU, vc];
    const b: Vec2 = sign < 0 ? [v.chordU, vc] : [v.u0, vc];
    const length = v.chordU - v.u0;
    rectWall(
      builder,
      styles,
      frame,
      [a, b],
      [0, v.eaveY],
      [...pointedWindows(length, 4.8, 1.3, 3.5), ...pointedWindows(length, 4.8, 5.8, 10)]
    );
  }
  horizontalCap(
    builder,
    styles.wall,
    frame,
    [
      [v.u0, lo],
      [v.chordU, lo],
      [v.chordU, hi],
      [v.u0, hi],
    ],
    0.02,
    true
  );
  gallery(builder, styles, p);
  buildVesselRoof(builder, styles, frame, v);
  belfry(builder, styles, p);
  return builder.triangleCount - before;
}
